import React from "react";
import { Box, Button, Stack } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import { BLOCK_BORDER_COLOR } from "../../shared/constants";

interface TargetActionBarProps {
  isEdit: boolean;
  isSubmitting: boolean;
  /** Save/Create is disabled until the form is valid. */
  canSubmit: boolean;
  onDiscard: () => void;
  onSubmit: () => void;
}

/**
 * Sticky action bar pinned to the bottom of the viewport while the user scrolls
 * the form. Matches the Figma `target action bar` token: semi-transparent white
 * pill with a 16px radius and a stacked drop shadow.
 */
const TargetActionBar: React.FC<TargetActionBarProps> = ({
  isEdit,
  isSubmitting,
  canSubmit,
  onDiscard,
  onSubmit,
}) => (
  <Box
    component="footer"
    sx={{
      position: "sticky",
      bottom: 16,
      mt: 4,
      zIndex: 10,
      // Center the bar within the form column on wide screens.
      display: "flex",
      justifyContent: "flex-end",
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      gap={3}
      sx={{
        bgcolor: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(8px)",
        border: `1px solid ${BLOCK_BORDER_COLOR}`,
        borderRadius: 4,
        p: 4,
        boxShadow: "0 10px 15px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.1)",
        width: "100%",
      }}
    >
      <Button
        variant="text"
        color="inherit"
        onClick={onDiscard}
        disabled={isSubmitting}
        sx={{ color: "text.secondary" }}
        aria-label="Discard changes"
      >
        Discard Changes
      </Button>
      <LoadingButton
        variant="contained"
        color="primary"
        loading={isSubmitting}
        disabled={!canSubmit || isSubmitting}
        onClick={onSubmit}
        aria-label={isEdit ? "Save changes" : "Create target"}
      >
        {isEdit ? "Save Changes" : "Create Target"}
      </LoadingButton>
    </Stack>
  </Box>
);

export default TargetActionBar;
